/**
 * Clients Reports Page
 * Página de relatórios de clientes
 */

import { useState, useEffect } from 'react';
import type { ComponentType } from 'react';
import { FileText, Download, Filter, BarChart3, Users, TrendingUp } from 'lucide-react';
import { reportsService } from '../services/reports-service';
import type { ReportDefinition, ReportResult } from '../services/reports-service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { FormField } from '@/components/common/FormSection';
import { cn } from '@/utils/cn';
import { getErrorMessage } from '../utils/error.utils';
import { parseDbTimestamp } from '../utils/datetime';

export function ClientsReportsPage() {
  const [definition, setDefinition] = useState<ReportDefinition | null>(null);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parâmetros do filtro
  const [status, setStatus] = useState<string>('');
  const [clientType, setClientType] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Cargar definição do relatório
  useEffect(() => {
    loadReportDefinition();
  }, []);

  const loadReportDefinition = async () => {
    try {
      const def = await reportsService.getReportDefinition('clients_list');
      setDefinition(def);
    } catch (err: unknown) {
      console.error('Error loading report definition:', err);
      setError('Erro ao carregar definição do relatório');
    }
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const parameters: Record<string, unknown> = {};
      if (status) parameters.status = status;
      if (clientType) parameters.clientType = clientType;
      if (dateFrom) parameters.dateFrom = dateFrom;
      if (dateTo) parameters.dateTo = dateTo;

      const reportResult = await reportsService.generateReport({
        reportCode: 'clients_list',
        parameters
      });

      setResult(reportResult);
    } catch (err: unknown) {
      console.error('Error generating report:', err);
      setError(getErrorMessage(err, 'Erro ao gerar relatório'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      setLoading(true);

      const parameters: Record<string, unknown> = {};
      if (status) parameters.status = status;
      if (clientType) parameters.clientType = clientType;
      if (dateFrom) parameters.dateFrom = dateFrom;
      if (dateTo) parameters.dateTo = dateTo;

      const blob = await reportsService.exportReport({
        reportCode: 'clients_list',
        parameters,
        format
      });

      const filename = `relatorio-clientes-${Date.now()}.${format}`;
      reportsService.downloadFile(blob, filename);
    } catch (err: unknown) {
      console.error('Error exporting report:', err);
      setError('Erro ao exportar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStatus('');
    setClientType('');
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR') ?? '-';
  };

  if (!definition && !error) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando relatório...</span>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!definition && error) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <div>
            <p className="font-medium text-destructive">Erro ao carregar relatório</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadReportDefinition}>Tentar novamente</Button>
        </div>
      </div>
    );
  }

  if (!definition) return null;

  const agg = result?.aggregations as {
    total?: number
    byStatus?: { active?: number }
    byType?: { person?: number; company?: number }
  } | undefined;

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <FileText className="size-6 shrink-0 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">{definition.name}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{definition.description}</p>
      </div>

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 text-base font-medium">
            <Filter className="size-4 text-muted-foreground" aria-hidden="true" />
            Filtros
          </h2>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Status" htmlFor="clients-report-status">
              <NativeSelect id="clients-report-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
              </NativeSelect>
            </FormField>
            <FormField label="Tipo de Cliente" htmlFor="clients-report-type">
              <NativeSelect id="clients-report-type" value={clientType} onChange={(e) => setClientType(e.target.value)}>
                <option value="">Todos</option>
                <option value="PERSON">Pessoa Física</option>
                <option value="COMPANY">Pessoa Jurídica</option>
              </NativeSelect>
            </FormField>
            <FormField label="Data Inicial" htmlFor="clients-report-from">
              <Input id="clients-report-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </FormField>
            <FormField label="Data Final" htmlFor="clients-report-to">
              <Input id="clients-report-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </FormField>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleGenerateReport} loading={loading}>
              {loading ? 'Gerando...' : (
                <>
                  <BarChart3 className="size-4" />
                  Gerar Relatório
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <p className="font-medium text-destructive">Erro</p>
          <p className="text-muted-foreground">{error}</p>
        </div>
      )}

      {agg && (
        <KpiGrid cols={4} gap={4}>
          <StatTile label="Total de Clientes" value={agg.total ?? 0} icon={Users} />
          <StatTile label="Ativos" value={agg.byStatus?.active ?? 0} icon={TrendingUp} tone="text-primary" />
          <StatTile label="Pessoa Física" value={agg.byType?.person ?? 0} icon={Users} />
          <StatTile label="Pessoa Jurídica" value={agg.byType?.company ?? 0} icon={Users} />
        </KpiGrid>
      )}

      {result && (
        <Card className="min-w-0">
          <CardHeader className="border-b">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h2 className="text-base font-medium">Resultados</h2>
                <p className="text-sm text-muted-foreground">
                  {result.metadata.totalRecords} registro(s) encontrado(s) em {result.metadata.executionTime}ms
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport('csv')} disabled={loading}>
                  <Download className="size-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExport('json')} disabled={loading}>
                  <Download className="size-4" />
                  JSON
                </Button>
              </div>
            </div>
          </CardHeader>

          {result.data.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nenhum resultado encontrado</p>
          ) : (
            <div className="min-w-0 px-2">
              <Table className="min-w-[860px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Nome Completo</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>WhatsApp</TableHead>
                    <TableHead>Data Cadastro</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.data.map((row, index) => {
                    const r = row as Record<string, string | number | null | undefined>
                    return (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{r.client_number}</TableCell>
                        <TableCell>{r.full_name}</TableCell>
                        <TableCell className="text-muted-foreground">{r.client_type}</TableCell>
                        <TableCell>
                          <Badge variant={r.status === 'Ativo' ? 'default' : 'outline'}>{r.status}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{r.email}</TableCell>
                        <TableCell className="text-muted-foreground">{r.whatsapp}</TableCell>
                        <TableCell className="text-muted-foreground tabular-nums">
                          {formatDate(String(r.created_at ?? ''))}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}

      {!result && !loading && !error && (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-12 text-center">
          <div className="mb-2 flex size-14 items-center justify-center rounded-full bg-muted">
            <BarChart3 className="size-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <h3 className="text-base font-medium">Configure os filtros e gere o relatório</h3>
          <p className="text-sm text-muted-foreground">
            Use os filtros acima para refinar os resultados e clique em &quot;Gerar Relatório&quot;
          </p>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, tone }: {
  label: string
  value: number
  icon: ComponentType<{ className?: string }>
  tone?: string
}) {
  return (
    <Card size="sm">
      <CardContent className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={cn('text-2xl font-semibold tabular-nums', tone)}>{value}</p>
        </div>
        <Icon className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
      </CardContent>
    </Card>
  );
}
